import { get } from 'lodash';
import { BasketModel } from './basket.model';
import { MONEY_SYMBOLS } from '../../../core/money';
import { RateHelper, VAT_INCLUDED_MESSAGE } from '../../rate/model/rate.helper';
import {
  RoomBasketModel,
  RoomImageMetadata,
  RoomModel,
  RoomOccupancyDefinition,
  RoomTemplateImage,
} from '../../room/model';

const getZeroBasketValue = (basket: BasketModel) => {
  return {
    value: { amount: 0.0, currency: basket.currency },
    text: `${
      MONEY_SYMBOLS[basket.currency as keyof typeof MONEY_SYMBOLS] ||
      basket.currency
    } 0`,
  };
};

const getTotalOriginalPrice = (basket: BasketModel) => {
  if (get(basket.price, 'totalPrice.original.text')) {
    return basket.price.totalPrice.original;
  }

  return getZeroBasketValue(basket);
};

const getTotalConvertedPrice = (basket: BasketModel) => {
  if (get(basket.price, 'totalPrice.converted.text')) {
    return basket.price.totalPrice.converted;
  }

  return getZeroBasketValue(basket);
};

const getNumberOfAccommodation = (basket: BasketModel) => {
  return basket.rooms.reduce((acc, room) => {
    for (const occId in room.occupancies) {
      const unitAccommodation = getUnitAccomodation(
        room.occupancies[occId].definition
      );
      const qty = room.occupancies[occId].selectedQuantity;
      acc += unitAccommodation * qty;
    }
    return acc;
  }, 0);
};

const getOccupancyOfAccommodation = (basket: BasketModel) => {
  // TODO REVIEW
  return basket.rooms.reduce((acc: any, room: any) => {
    return [
      ...acc,
      ...Object.keys(room.occupancies).map((occId) => {
        const accommodationOccupancy = room.occupancies[occId].definition.value;
        const qty = room.occupancies[occId].selectedQuantity;
        const accommodationOccupancyTotalQuantity =
          accommodationOccupancy.isDetailed
            ? {
                adultCount: accommodationOccupancy.adultCount * qty,
                childCount: accommodationOccupancy.childCount * qty,
                infantCount: accommodationOccupancy.infantCount * qty,
              }
            : {
                personCount: accommodationOccupancy.personCount * qty,
              };
        return {
          id: occId,
          occupancy: accommodationOccupancyTotalQuantity,
        };
      }),
    ];
  }, []) as Array<{
    id: string;
    occupancy:
      | { adultCount: number; childCount: number; infantCount: number }
      | { personCount: number };
  }>;
};

const getUnitAccomodation = (
  roomOccupancyDefinition: RoomOccupancyDefinition
) => {
  const roomDetailedOccupancyTotal =
    roomOccupancyDefinition.value.adultCount +
    roomOccupancyDefinition.value.childCount +
    roomOccupancyDefinition.value.infantCount;
  // console.log('roomOccupancyDefinition value: ', roomOccupancyDefinition.value);
  return roomOccupancyDefinition.value.isDetailed
    ? roomDetailedOccupancyTotal
    : roomOccupancyDefinition.value.personCount;
};

const getQuantityByBasketRoom = (
  room: RoomBasketModel,
  occId = getFirstOccupancyIdInBasketRoom(room)
) => {
  return room.occupancies[occId].selectedQuantity;
};

const getNumberOfRooms = (basket: Partial<BasketModel>) => {
  return (
    basket.rooms?.reduce((acc, room) => {
      return (
        acc +
        Object.values(room.occupancies).reduce(
          (_sum, occ) => getQuantityByBasketRoom(room, `${occ.occupancyId}`),
          0
        )
      );
    }, 0) || 0
  );
};

const getTaxesFormatted = (basket: BasketModel) => {
  if (basket.taxes.excluded.value.amount === 0) {
    return VAT_INCLUDED_MESSAGE;
  }

  return basket.taxes.excluded.text;
};

const getRoomCoverImage = (basketRoom: RoomBasketModel) => {
  if (basketRoom.pictures.length) {
    return basketRoom.pictures[0].templates.find(
      (t) => t.name === RoomTemplateImage.XLarge
    );
  }

  return {} as RoomImageMetadata;
};

const getFirstOccupancyIdInBasketRoom = (basketRoom: RoomBasketModel) => {
  const occupancyIds = Object.keys(basketRoom.occupancies);
  return occupancyIds && occupancyIds[0];
};

const getRoomTotalPrice = (basketRoom: RoomBasketModel) => {
  const occId = getFirstOccupancyIdInBasketRoom(basketRoom);
  const occupancy = basketRoom.occupancies[occId];
  return RateHelper.multiplyMoney(
    occupancy.price.converted,
    occupancy.selectedQuantity
  );
};

const roomToBasketRoom = (
  room: RoomModel,
  rateId: string,
  selectedQuantity: number
): RoomBasketModel => {
  return {
    roomId: room.roomId,
    name: room.name,
    pictures: room.pictures,
    occupancies: room.occupancies.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.occupancyId]: { ...curr, rateId, selectedQuantity },
      }),
      {}
    ),
    defaultOccupancy: room.occupancies.find((o) => o.isDefault)!,
    beds: room.bedding.beds,
    amenities: [],
    extras: {},
    productId: '',
    summary: [],
    type: '',
    surfaceArea: { text: '', value: '' },
  };
};

export const BasketHelper = {
  getTotalOriginalPrice,
  getTotalConvertedPrice,
  getNumberOfAccommodation,
  getOccupancyOfAccommodation,
  getNumberOfRooms,
  getTaxesFormatted,
  getRoomCoverImage,
  getFirstOccupancyIdInBasketRoom,
  getQuantityByBasketRoom,
  getRoomTotalPrice,
  roomToBasketRoom,
};
